import * as Sentry from "@sentry/react"
import Cookies from "js-cookie"

/** The cookie the Confidence SDK reads and writes its visitor id in. */
const VISITOR_ID_COOKIE = "cnfdVisitorId"

/** Mirror of the cookie, for the browsers that drop it. */
const VISITOR_ID_STORAGE_KEY = "cnfdVisitorId"

/** Matches the five years the SDK asks for, so persisting never shortens it. */
const VISITOR_ID_COOKIE_EXPIRY_DAYS = 365 * 5

/**
 * Carries the id across a redirect that leaves the origin and comes back.
 *
 * Paying hands the browser to Stripe and returns to the completion page as a
 * fresh load, and that round trip loses the id: the returning page finds no
 * cookie and no mirror, mints a new one, and the purchase is credited to
 * somebody who never saw the funnel. Measured on Lingo web, which runs this
 * same checkout, it happens on 23% of purchases.
 *
 * Writing is not the problem — no storage failure is ever reported, and
 * Snowplow's own cookie survives the same bounce on 99.7% of returns. The
 * browser simply does not keep this one, so the return URL is the only carrier
 * left that it cannot drop.
 */
export const VISITOR_ID_QUERY_PARAM = "cnfd_visitor_id"

/**
 * The only page a payment redirect can land on, and so the only page allowed
 * to hand us an id.
 *
 * Without this the parameter is honoured everywhere, and a first-time visitor
 * has neither cookie nor mirror — so any link carrying `?cnfd_visitor_id=`
 * would plant that id and persist it. Shared innocently, that collapses many
 * visitors onto one unit and corrupts every experiment they touch; shared
 * deliberately, it assigns the sender's unit to whoever follows the link.
 * Restricting it to the page the redirect actually lands on keeps the carrier
 * without opening the id up to the whole funnel.
 */
const VISITOR_ID_QUERY_PATHNAME = "/checkout/payment-completion"

/** The shape `crypto.randomUUID` produces. Anything else did not come from us. */
const VISITOR_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The visitor id for this page load, resolved once and then never re-read from
 * the browser.
 *
 * This is the part that fixes iOS. Measured on the Lingo funnel, which runs
 * this same code, the id was present on the first page view and gone from the
 * second onward for 29.7% of Facebook and 37.1% of Instagram in-app browsers
 * on iOS, against 0.0% on every Android and desktop browser. Routing is
 * client-side, so those views share a single document: the cookie was being
 * evicted between two reads of the same page load, and a storage mirror did
 * not survive there either.
 *
 * A module-scoped variable cannot be evicted by any of that, so only the first
 * read touches the browser at all and every later read is served from here.
 * Storage is still written, but it is an optimisation for the next page load
 * rather than something correctness depends on.
 *
 * After this shipped to Lingo both iOS figures fell to 0.0% while the Android
 * control stayed at 0.0%, and signup coverage went from 80.6% to 100%.
 */
let resolvedVisitorId: string | undefined

/**
 * Every path prefix of the current URL.
 *
 * Cookie paths cannot be read back from JavaScript, so a copy written under a
 * narrower path can only be removed by naming that path. The prefixes of the
 * current URL are exactly the paths whose cookies are visible here, which makes
 * them the only ones that can be shadowing the root cookie on this page.
 */
function currentPathPrefixes(): string[] {
  const segments = window.location.pathname.split("/").filter(Boolean)

  return segments.map(
    (_, index) => `/${segments.slice(0, index + 1).join("/")}`,
  )
}

function readVisitorIdCookie(): string | undefined {
  try {
    return Cookies.get(VISITOR_ID_COOKIE)
  } catch {
    // Cookie access throws in a sandboxed frame. Treated as absent, because
    // the mirror below and a freshly minted id both still work.
    return undefined
  }
}

function readMirroredVisitorId(): string | undefined {
  try {
    return localStorage.getItem(VISITOR_ID_STORAGE_KEY) ?? undefined
  } catch {
    // Web Storage throws rather than returning null wherever it is
    // unavailable — no quota in Safari Private Browsing, denied outright in
    // some in-app webviews.
    return undefined
  }
}

/**
 * The id handed back by a redirect, if this URL carries a well-formed one.
 *
 * Validated rather than trusted: this is a URL, so anyone can put anything in
 * it, and an id that is not shaped like ours would pollute the warehouse and
 * bucket the visitor into a variant on a value we never issued. Shape is not
 * provenance though — a well-formed uuid can still be someone else's, which is
 * why the pathname is checked too.
 */
function readVisitorIdFromUrl(): string | undefined {
  try {
    if (window.location.pathname !== VISITOR_ID_QUERY_PATHNAME) return undefined

    const value = new URLSearchParams(window.location.search).get(
      VISITOR_ID_QUERY_PARAM,
    )

    return value && VISITOR_ID_PATTERN.test(value) ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * The id handed over by the payment redirect, captured at module load.
 *
 * Read here rather than when an id is first needed, because by then the
 * address bar has usually moved on. The completion page navigates away as soon
 * as its loader settles — to the account page on success, back to payment
 * otherwise — and an unauthenticated load never renders there at all, since
 * the payment route redirects to sign-in. A pathname check performed at first
 * use therefore rejects a parameter that was legitimately ours, which is
 * exactly what happened when this first shipped: a clean browser opening a
 * completion URL minted a new id instead of adopting the one it had been
 * handed.
 *
 * Module evaluation runs before React renders and before the router can
 * navigate, so this is the only moment the landing URL is reliably still the
 * one the browser was given.
 */
const redirectVisitorId = readVisitorIdFromUrl()

function createVisitorId(): string | undefined {
  // Only available in a secure context, which production is; a funnel served
  // over plain http gets no id rather than a predictable one.
  if (typeof crypto?.randomUUID !== "function") return undefined

  return crypto.randomUUID()
}

/**
 * Writes the cookie and reports whether it can be read back again.
 *
 * The read-back is the point. `document.cookie` reports no error when a browser
 * declines to keep what it was given, which is exactly how the id was being
 * lost on iOS in-app browsers, so trusting the write would leave us blind to
 * the one thing worth knowing.
 */
function writeVisitorIdCookie(visitorId: string): boolean {
  try {
    // The SDK writes this cookie with no `path`, so it lands on the directory
    // of whichever URL loaded first and is invisible under any other prefix.
    // Clearing the narrower copies before writing at the root leaves one value
    // per browser instead of one per funnel section, which is what stops the
    // same person accumulating several ids.
    for (const path of currentPathPrefixes()) {
      Cookies.remove(VISITOR_ID_COOKIE, { path })
    }

    Cookies.set(VISITOR_ID_COOKIE, visitorId, {
      expires: VISITOR_ID_COOKIE_EXPIRY_DAYS,
      path: "/",
      sameSite: "lax",
    })

    return Cookies.get(VISITOR_ID_COOKIE) === visitorId
  } catch {
    return false
  }
}

function writeMirroredVisitorId(visitorId: string): boolean {
  try {
    localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId)

    return localStorage.getItem(VISITOR_ID_STORAGE_KEY) === visitorId
  } catch {
    return false
  }
}

/** `"unavailable"` rather than a throw, so a tag can never break the report. */
function describeSafely(read: () => string): string {
  try {
    return read()
  } catch {
    return "unavailable"
  }
}

/**
 * Size and count of the cookie jar. Counts and sizes only — never a cookie
 * name or value.
 *
 * Reported as `"unavailable"` when `document.cookie` itself throws, which is
 * worth recording rather than crashing over: it distinguishes a frame that
 * cannot touch cookies at all from a domain that is merely full.
 */
function describeCookieJar(): { cookieBytes: string; cookieCount: string } {
  try {
    const cookie = document.cookie

    return {
      cookieBytes: String(cookie.length),
      cookieCount: String(cookie ? cookie.split("; ").length : 0),
    }
  } catch {
    return { cookieBytes: "unavailable", cookieCount: "unavailable" }
  }
}

/**
 * Reports a browser that would not keep the id.
 *
 * Fires on the condition itself rather than on a downstream symptom of it.
 * What it answers:
 *
 * - `cookieStored` false: this browser drops the cookie even at `path=/` with
 *   `SameSite=Lax`, so the visitor is identified for this page load only and
 *   returns as somebody new. This is the measurement of whether the iOS fix
 *   holds; Sentry tags the browser and OS itself, so no user agent is sent.
 * - `mirrorStored` false: Web Storage is unavailable too, so nothing at all
 *   persists between page loads.
 * - `cookieCount` and `cookieBytes` near a per-domain ceiling: the write was
 *   refused because the domain is full rather than because of the browser.
 *
 * Runs at most once per page load, since the id is resolved once.
 */
function reportVisitorIdNotStored({
  cookieStored,
  mirrorStored,
}: {
  cookieStored: boolean
  mirrorStored: boolean
}): void {
  // Nothing in here may throw. This runs inside resolveConfidenceVisitorId,
  // which is called during render, so an exception would reach the route's
  // errorElement and take the funnel to the error page — the exact failure
  // this module exists to prevent, caused by the code observing it.
  //
  // Not a remote edge case either: the only way in is a failed write, and the
  // usual reason a write fails is that cookie access throws, so this is
  // reached most often in precisely the state where reading it throws too.
  try {
    // Read now, while they still describe the failure. Only the send is
    // deferred, and the cookie jar can change between the two.
    const messageContext = {
      extra: { pathname: describeSafely(() => window.location.pathname) },
      level: "warning" as const,
      tags: {
        ...describeCookieJar(),
        cookieEnabled: describeSafely(() => String(navigator.cookieEnabled)),
        cookieStored: String(cookieStored),
        mirrorStored: String(mirrorStored),
      },
    }

    sendWhenSentryReady(() => {
      Sentry.captureMessage(
        "Confidence visitor id could not be stored",
        messageContext,
      )
    })
  } catch {
    // The id is resolved and returned either way, so a failed report costs
    // visibility and nothing else.
  }
}

/**
 * Sends a report once Sentry is able to accept it.
 *
 * The first `resolveConfidenceVisitorId` call happens during module evaluation:
 * `helpers/confidence` calls it at module scope, `App.tsx` imports that module,
 * and an ES import is evaluated before the body of the module that imports it —
 * so this runs before the `Sentry.init` further down `App.tsx`. Sentry drops
 * anything captured while no client is bound, and the id is resolved once per
 * page load, so capturing inline discarded the report on exactly the page loads
 * worth measuring.
 *
 * `Sentry.init` is called synchronously inside that same startup task, so
 * waiting for the next macrotask is enough to land after it. One deferral rather
 * than a retry loop: if no client exists once startup has finished then none
 * ever will, which is the case in development where `init` is skipped and
 * dropping this is the wanted behaviour.
 */
function sendWhenSentryReady(send: () => void): void {
  // Guarded here as well as at the call site, because a deferred send runs on
  // its own task where the caller's try/catch no longer applies.
  const sendSafely = () => {
    try {
      send()
    } catch {
      // As above — a failed report costs visibility and nothing else.
    }
  }

  if (Sentry.getClient()) {
    sendSafely()
    return
  }

  setTimeout(sendSafely, 0)
}

/**
 * Writes the id everywhere it might survive to, without letting any single
 * failure matter.
 *
 * Both writes are best-effort by design: the caller already holds the id, and
 * the in-memory value is what the rest of this page load reads. Only whether
 * they succeeded is acted on, and only to report it.
 */
function persistVisitorId(visitorId: string): void {
  const cookieStored = writeVisitorIdCookie(visitorId)
  const mirrorStored = writeMirroredVisitorId(visitorId)

  if (!cookieStored || !mirrorStored) {
    reportVisitorIdNotStored({ cookieStored, mirrorStored })
  }
}

/**
 * The Confidence visitor id, minting one if the browser has none.
 *
 * An existing value always wins, so a visitor already carrying an id keeps it
 * and stays in whatever variant they were already served. Only a browser with
 * nothing to offer gets a new one.
 *
 * Returns undefined only where an id cannot exist at all — no `document`, or
 * no `crypto.randomUUID` — so callers can keep treating that as "not
 * identified" rather than inventing a placeholder.
 *
 * Resolution order is storage, then the redirect parameter, then a new id. See
 * `readVisitorIdFromUrl` for why the redirect is consulted last rather than
 * first, and `redirectVisitorId` for why it is read at module load.
 */
export function resolveConfidenceVisitorId(): string | undefined {
  if (resolvedVisitorId) return resolvedVisitorId

  if (typeof document === "undefined") return undefined

  // Storage first, the URL only after it. Whatever this browser already holds
  // is its real identity, so a link someone pasted to a friend cannot
  // transplant an id onto a visitor who already has one. The redirect value is
  // consulted exactly where it helps: a browser that kept nothing across the
  // payment bounce, which would otherwise mint a stranger.
  resolvedVisitorId =
    readVisitorIdCookie() ??
    readMirroredVisitorId() ??
    redirectVisitorId ??
    createVisitorId()

  if (resolvedVisitorId) persistVisitorId(resolvedVisitorId)

  return resolvedVisitorId
}
